import type { ToolDefinition } from '../types';
import { CameraPanel } from './panel';
import { DEFAULT_CAMERA_PARAMS, normalizeCameraParams, type CameraParams } from './types';

function normalizeAngle360(deg: number): number {
    const n = ((deg % 360) + 360) % 360;
    return Object.is(n, -0) ? 0 : n;
}

// ===== Legacy prompt (non-continuous) =====
const LEGACY_AZIMUTH_MAP: Record<number, string> = {
    0: "front view",
    45: "front-right quarter view",
    90: "right side view",
    135: "back-right quarter view",
    180: "back view",
    225: "back-left quarter view",
    270: "left side view",
    315: "front-left quarter view"
};

const LEGACY_PITCH_MAP: Record<number, string> = {
    '-30': "low-angle shot",
    0: "eye-level shot",
    30: "elevated shot",
    60: "high-angle shot"
};

const LEGACY_DISTANCE_MAP: Record<string, string> = {
    '0.6': "close-up",
    '1.0': "medium shot",
    '1.4': "wide shot"
};

function snapToNearest(value: number, options: number[]): number {
    return options.reduce((prev, curr) => Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev);
}

function legacyPrompt(params: Pick<CameraParams, 'azimuth' | 'elevation' | 'distance'>): string {
    const azKeys = Object.keys(LEGACY_AZIMUTH_MAP).map(Number);
    const pitchKeys = Object.keys(LEGACY_PITCH_MAP).map(Number);
    const distKeys = Object.keys(LEGACY_DISTANCE_MAP).map(Number);

    const azNorm = normalizeAngle360(params.azimuth);
    // elevation is stored as "仰角" where 90=eye level -> pitch = elevation - 90
    const pitch = ((normalizeAngle360(params.elevation) - 90 + 540) % 360) - 180;

    const azSnap = snapToNearest(azNorm, azKeys);
    const pitchSnap = snapToNearest(pitch, pitchKeys);
    const distSnap = snapToNearest(params.distance, distKeys);

    const azName = LEGACY_AZIMUTH_MAP[azSnap];
    const pitchName = LEGACY_PITCH_MAP[pitchSnap];
    const distName = LEGACY_DISTANCE_MAP[distSnap.toFixed(1)];

    return `<sks> ${azName} ${pitchName} ${distName}`;
}

// ===== Continuous prompt (your new template) =====
function continuousPrompt(azimuth: number, elevation: number, distance: number): string {
    // azimuth: 0–360 (0/360 = front)
    const az = normalizeAngle360(azimuth);
    // elevation: "仰角" 0–360, 90 = eye level
    const el = normalizeAngle360(elevation);
    const dist = Number.isFinite(distance) ? Number(distance) : 1.0;

    const azPhrase = (() => {
        const a = Math.round(az);
        if (a <= 5 || a >= 356) return 'from the front facing the subject';
        if (a >= 6 && a <= 89) return `from the subject's right side at ${a}°`;
        if (a >= 90 && a <= 134) return "from the subject's rear-right side";
        if (a >= 135 && a <= 225) return 'from directly behind the subject';
        if (a >= 226 && a <= 270) return "from the subject's rear-left side";
        if (a >= 271 && a <= 355) return `from the subject's left side at ${Math.max(0, 360 - a)}°`;
        return 'from the front facing the subject';
    })();

    const elPhrase = (() => {
        const e = Math.round(el);
        if (e <= 87) return `looking up from below ${Math.max(0, 90 - e)}°`;
        if (e >= 88 && e <= 92) return 'at eye level';
        return `looking down from above ${Math.max(0, e - 90)}°`;
    })();

    const distPhrase = (() => {
        const d = dist;
        if (d <= 0.8) return 'as a extreme close-up shot';
        if (d <= 1.2) return 'as a close-up shot';
        if (d <= 2) return 'as a medium shot';
        if (d <= 2.8) return 'as a medium-wide shot';
        return 'as a wide establishing shot';
    })();

    return `The main subject to be photographed. Imagine reshooting this photo, ${azPhrase}, ${elPhrase}, ${distPhrase}. Appropriately adjust the background to match the new camera angle, focal length, and perspective, ensuring natural coordination between the background and subject.`;
}

function generateCameraPrompt(params: CameraParams): string {
    return params.continuous
        ? continuousPrompt(params.azimuth, params.elevation, params.distance)
        : legacyPrompt(params);
}

export const cameraTool: ToolDefinition<CameraParams> = {
    id: 'camera',
    label: 'tool.camera.name',
    defaultParams: DEFAULT_CAMERA_PARAMS,
    normalize: normalizeCameraParams,
    Panel: ({ params, onChange, nodeId }) => {
        const handleChange = (patch: Partial<CameraParams>) => {
            const newParams = { ...params, ...patch };
            const prompt = generateCameraPrompt(newParams as CameraParams);
            onChange({ ...patch, prompt });
        };
        return <CameraPanel nodeId={nodeId} params={params} onChange={handleChange} />;
    },
    onRun: async ({ params, now }) => {
        const sourceUrl = typeof (params as any)?.source_image_url === 'string' ? String((params as any).source_image_url).trim() : '';
        if (!sourceUrl) {
            // Camera tool requires an upstream image; UI should prevent this, but keep runtime safe.
            return { connectFromSource: false, autoRun: false };
        }

        const rawModelId =
            (typeof (params as any)?.model_id === 'string' && String((params as any).model_id).trim())
                ? String((params as any).model_id).trim()
                : 'wavespeed-ai/qwen-image/edit-2511-lora-multiple-angles';

        const NANO_RESOLUTION: Record<string, string> = {
            'nano-banana-2-1k': '1K',
            'nano-banana-2-2k': '2K',
            'nano-banana-2-4k': '4K',
        };
        const isNanoBanana = rawModelId in NANO_RESOLUTION;
        const MODEL_ID = isNanoBanana ? 'nano-banana-pro' : rawModelId;
        const imageResolution = isNanoBanana ? NANO_RESOLUTION[rawModelId] : undefined;

        return {
            createNode: {
                type: 'image',
                data: {
                    name: 'Camera',
                    viewMode: 'transform',
                    model: MODEL_ID,
                    // Image input is provided via the tool->image edge (tool node keeps source_image_url in sync).
                    // Prefer match for i2i if the model supports it.
                    size: 'match',
                    // Ensure auto-run has a valid resolution immediately (ImageTransform may set defaults later).
                    image_resolution: imageResolution,
                    updatedAt: now,
                    generated: undefined,
                    status: undefined,
                    processingTaskId: undefined,
                    processingGenerationId: undefined,
                } as any,
            },
            connectFromSource: true,
            autoRun: true,
        };
    },
};

export type { CameraParams };
export { DEFAULT_CAMERA_PARAMS, normalizeCameraParams };
